import moment from 'moment-timezone';

import { EActions } from '../../redux/actions/EActions';
import { NewUser } from '../../models';
import { Organization } from '../../models/organization';

export enum SubscriptionStatus {
  Unsubscribed = 'Unsubscribed', // Initial state
  Active = 'Active',
  Trial = 'Trial',
  Incomplete = 'Incomplete',
  PastDue = 'PastDue',
  IncompleteExpired = 'IncompleteExpired',
  Cancelled = 'Cancelled',
  Unpaid = 'Unpaid',
  Free = 'Free',
}

export interface Subscription {
  idPlanOffer?: string;
  idOffer: string;
  isDefault: boolean;
  hasAccess: boolean;
  status: SubscriptionStatus;
  daysFreeTrialLeft: number;
}

export enum Scope {
  ContactsCreate = 'contacts:create',
  ContactsRead = 'contacts:read',
  ContactsUpdate = 'contacts:update',
  ContactsDelete = 'contacts:delete',

  AttachmentsCreate = 'attachments:create',
  AttachmentsRead = 'attachments:read',
  AttachmentsUpdate = 'attachments:update',
  AttachmentsDelete = 'attachments:delete',

  AgreementsCreate = 'agreements:create',
  AgreementsRead = 'agreements:read',
  AgreementsUpdate = 'agreements:update',
  AgreementsDelete = 'agreements:delete',
  AgreementsComplete = 'agreements:complete',
  AgreementsNotify = 'agreements:notify',

  RatesCreate = 'rates:create',
  RatesRead = 'rates:read',
  RatesUpdate = 'rates:update',
  RatesDelete = 'rates:delete',

  ReceiptsCreate = 'receipts:create',
  ReceiptsRead = 'receipts:read',
  ReceiptsUpdate = 'receipts:update',
  ReceiptsDelete = 'receipts:delete',

  ExpensesCreate = 'expenses:create',
  ExpensesRead = 'expenses:read',
  ExpensesUpdate = 'expenses:update',
  ExpensesDelete = 'expenses:delete',

  ShiftsCreate = 'shifts:create',
  ShiftsRead = 'shifts:read',
  ShiftsUpdate = 'shifts:update',
  ShiftsDelete = 'shifts:delete',
  ShiftsCancel = 'shifts:cancel',
  ShiftsComplete = 'shifts:complete',
  ShiftsAmend = 'shifts:amend',
  ShiftsNotify = 'shifts:notify',

  InvoicesCreate = 'invoices:create',
  InvoicesRead = 'invoices:read',
  InvoicesUpdate = 'invoices:update',
  InvoicesDelete = 'invoices:delete',
  InvoicesNotify = 'invoices:notify',

  IncidentsCreate = 'incidents:create',
  IncidentsDelete = 'incidents:delete',

  DashboardShiftsToday = 'dashboards:shifts:today',
  DashboardsShiftsIncompleted = 'dashboards:shifts:incompleted',
  DashboardsShiftsUninvoiced = 'dashboards:shifts:uninvoiced',
  DashboardsInvoicesOverdue = 'dashboards:invoices:overdue',
  DashboardsSummary = 'dashboards:summary',

  OrganizationManageSettings = 'organization:manage:settings',
  OrganizationManageStaff = 'organization:manage:staff',
  OrganizationManageSubscription = 'organization:manage:subscription',

  ReportsShift = 'reports:shift',
  ReportsTax = 'reports:tax',
  ReportsInvoice = 'reports:invoice',
  ReportsKms = 'reports:km',
  ReportsIncident = 'reports:incident',

  // Data type
  ExpensesTypeBusiness = 'expenses:type:business',
  ExpensesTypeReclaimable = 'expenses:type:reclaimable',

  // Filter through user data
  UsersDataAll = 'users:data:all',
}

export interface IAuthState {
  isAuthenticated: boolean;
  verified: boolean;
  created: boolean;
  error: string;
  success: string;
  isLoading: boolean;
  currentUser: NewUser;
  currentOrganization?: Organization;
  currentOrganizationUsers?: NewUser[];
  currentSubscription: Subscription;
  organizations: Organization[];
  scopes: Scope[];
}

const initalState = {
  isAuthenticated: false,
  verified: false,
  created: false,
  error: '',
  success: '',
  isLoading: false,
  currentUser: {} as NewUser,
  currentOrganization: undefined,
  currentOrganizationUsers: [],
  currentSubscription: {
    idOffer: '',
    // idPlanOffer: '',
    isDefault: true,
    hasAccess: false,
    status: SubscriptionStatus.Unsubscribed,
    daysFreeTrialLeft: 0,
  },
  organizations: [],
  scopes: [],
};

const reducer = (state: IAuthState = initalState, action: any) => {
  switch (action.type) {
    case EActions.SigninSuccess: {
      const currentUser = { ...state.currentUser, token: action.payload };
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        currentUser,
      };
    }
    case EActions.OrganizationSuccess: {
      const orgs = state.organizations.concat();
      orgs.push(action.payload);
      return {
        ...state,
        organizations: orgs,
      };
    }
    case EActions.OrganizationSwitch: {
      return {
        ...state,
        currentOrganization: action.payload,
        currentOrganizationUsers: [],
      };
    }

    //#region ChangeEmail
    case EActions.ChangeEmail:
      return {
        ...state,
        isLoading: true,
        success: '',
        error: '',
      };
    case EActions.ChangeEmailSuccess:
      if (action.payload) {
        const newUser = { ...state.currentUser, email: action.payload };
        return { ...state, isLoading: false, currentUser: newUser };
      }
      return { ...state, isLoading: false };
    case EActions.ChangeEmailFail:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    //#endregion

    //#region Me
    case EActions.MeSuccess: {
      const currentUser = { ...state.currentUser, ...action.payload.user };
      if (currentUser.dob) {
        currentUser.dob = moment(currentUser.dob);
      }
      return {
        ...state,
        isAuthenticated: true,
        currentUser,
        currentSubscription: action.payload.subscription,
        organizations: action.payload.organizations,
        currentOrganization: action.payload.organization,
        currentOrganizationUsers: [],
        scopes: action.payload.scopes,
      };
    }
    case EActions.MeFail:
      return { ...state, error: action.payload };

    case EActions.UpdateMe: {
      return { ...state, isLoading: true, success: '', error: '' };
    }
    case EActions.UpdateMeSuccess: {
      const currentUser = {
        ...state.currentUser,
        ...action.payload,
      };
      if (action?.payload?.dob) {
        currentUser.dob = moment(action.payload.dob);
      }
      return { ...state, isLoading: false, currentUser };
    }
    case EActions.UpdateMeFail:
      return { ...state, isLoading: false, error: action.payload };
    //#endregion

    //#region Organization
    case EActions.OrganizationUsers: {
      return { ...state, isLoading: true, currentOrganizationUsers: [] };
    }
    case EActions.OrganizationUsersSuccess: {
      return { ...state, isLoading: false, currentOrganizationUsers: action.payload };
    }
    case EActions.OrganizationUsersFail: {
      return { ...state, isLoading: false, error: action.payload };
    }
    //#endregion
  }
  return state;
};

export default reducer;
